import { ALLOWED_GROUPS, config } from './config';

/**
 * 直接调 Cognito 的 InitiateAuth / RespondToAuthChallenge。
 *
 * 不用 amazon-cognito-identity-js：那个库 2023 年后就没再发版，不认识 ESSENTIALS
 * 层级用户池的新挑战名（EMAIL_OTP、SELECT_CHALLENGE 等），遇到就会走到兜底分支
 * 去读不存在的 AuthenticationResult，报出 "Cannot read properties of undefined
 * (reading 'NewDeviceMetadata')" 这种和真实原因毫无关系的错。
 *
 * 这两个接口都是免鉴权的 JSON-RPC，用 fetch 调既没有凭证解析的坑，也不用背 SDK 体积。
 */

const ENDPOINT = `https://cognito-idp.${config.region}.amazonaws.com/`;
const STORAGE_KEY = 'cozy.admin.tokens';

type Tokens = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
};

export type Session = {
  email: string;
  username: string;
  groups: string[];
  accessToken: string;
};

/** 需要用户再输入点什么才能继续登录 */
export type Challenge = {
  name: string;
  session: string;
  username: string;
  prompt: string;
  inputKind: 'code' | 'password' | 'select';
  options?: string[];
};

export type SignInResult =
  | { kind: 'session'; session: Session }
  | { kind: 'challenge'; challenge: Challenge };

export class AuthError extends Error {
  /** Cognito 的异常名。续期时要靠它区分"令牌真的废了"和"这次没打通" */
  constructor(message: string, readonly type = '') {
    super(message);
  }
}

async function cognito(target: string, body: unknown) {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const type = String(payload.__type ?? '').split('#').pop() ?? '';
    throw new AuthError(friendlyError(type, payload.message), type);
  }
  return payload;
}

function friendlyError(type: string, message?: string) {
  switch (type) {
    case 'NotAuthorizedException':
      return 'Wrong email or password';
    case 'UserNotFoundException':
      return 'No account with that email';
    case 'CodeMismatchException':
      return 'That code is not correct';
    case 'ExpiredCodeException':
      return 'That code has expired. Sign in again to get a new one.';
    case 'InvalidPasswordException':
      return `The new password does not meet the requirements: ${message ?? ''}`;
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    default:
      return message ? `${type || 'Sign-in failed'}: ${message}` : (type || 'Sign-in failed');
  }
}

function decodeJwt(token: string): Record<string, unknown> {
  const [, payload] = token.split('.');
  if (!payload) return {};
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(decodeURIComponent(
    json.split('').map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
  ));
}

function parseGroups(claim: unknown): string[] {
  if (Array.isArray(claim)) return claim as string[];
  if (typeof claim === 'string') {
    return claim.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
  }
  return [];
}

/* 三个存取函数都不让异常漏出去：隐私模式或禁用了站点数据时，
   连读 localStorage 都会抛 SecurityError，不该把整个引导流程带崩 */

function store(tokens: Tokens) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch { /* 存不下就退化成单次会话，功能照常 */ }
}

function load(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function clear() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* 读不到自然也清不掉，忽略 */ }
}

function toSession(tokens: Tokens): Session {
  // 组信息在 access token 里（后端授权器读它），邮箱在 id token 里（只用于显示）
  const access = decodeJwt(tokens.accessToken);
  const id = decodeJwt(tokens.idToken);
  return {
    email: (id.email as string) ?? (access.username as string) ?? 'Unknown user',
    username: (access.username as string) ?? (id['cognito:username'] as string) ?? '',
    groups: parseGroups(access['cognito:groups'] ?? id['cognito:groups']),
    accessToken: tokens.accessToken,
  };
}

function fromAuthResult(result: {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}): Session {
  const tokens: Tokens = {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? load()?.refreshToken ?? '',
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  };
  store(tokens);
  return toSession(tokens);
}

/** 每个挑战需要用户输入什么、对应的 ChallengeResponses 键名 */
const CHALLENGE_SPEC: Record<string, {
  prompt: string;
  inputKind: Challenge['inputKind'];
  responseKey: string;
}> = {
  EMAIL_OTP: {
    prompt: 'We sent a code to your email. Enter it to continue.',
    inputKind: 'code',
    responseKey: 'EMAIL_OTP_CODE',
  },
  SMS_OTP: {
    prompt: 'We sent a code to your phone. Enter it to continue.',
    inputKind: 'code',
    responseKey: 'SMS_OTP_CODE',
  },
  SMS_MFA: {
    prompt: 'Enter the code from the text message.',
    inputKind: 'code',
    responseKey: 'SMS_MFA_CODE',
  },
  SOFTWARE_TOKEN_MFA: {
    prompt: 'Enter the 6-digit code from your authenticator app.',
    inputKind: 'code',
    responseKey: 'SOFTWARE_TOKEN_MFA_CODE',
  },
  NEW_PASSWORD_REQUIRED: {
    prompt: 'First sign-in. Choose a new password '
      + '(at least 12 characters, with upper and lower case letters and a digit).',
    inputKind: 'password',
    responseKey: 'NEW_PASSWORD',
  },
  SELECT_CHALLENGE: {
    prompt: 'Choose how to verify.',
    inputKind: 'select',
    responseKey: 'ANSWER',
  },
  SELECT_MFA_TYPE: {
    prompt: 'Choose how to verify.',
    inputKind: 'select',
    responseKey: 'ANSWER',
  },
};

const CHALLENGE_LABEL: Record<string, string> = {
  EMAIL_OTP: 'Email code',
  SMS_OTP: 'Text message code',
  SMS_MFA: 'Text message code',
  SOFTWARE_TOKEN_MFA: 'Authenticator code',
  PASSWORD: 'Password',
  PASSWORD_SRP: 'Password (SRP)',
};

export function challengeLabel(name: string) {
  return CHALLENGE_LABEL[name] ?? name;
}

/**
 * 把 Cognito 的响应统一成"拿到会话"或"还需要一步输入"。
 *
 * 遇到没预置说明的挑战不会崩，而是把挑战名原样显示出来——真出现新挑战时，
 * 登录页会直接告诉我们它叫什么，而不是抛一句读不懂的 TypeError。
 */
function interpret(response: any, username: string): SignInResult {
  if (response.AuthenticationResult) {
    return { kind: 'session', session: fromAuthResult(response.AuthenticationResult) };
  }

  const name = response.ChallengeName as string | undefined;
  if (!name) throw new AuthError('Cognito returned neither tokens nor a challenge');

  const spec = CHALLENGE_SPEC[name];
  const options: string[] | undefined = response.AvailableChallenges
    ?? (response.ChallengeParameters?.MFAS_CAN_CHOOSE
      ? JSON.parse(response.ChallengeParameters.MFAS_CAN_CHOOSE)
      : undefined);

  if (name === 'MFA_SETUP') {
    const canSetup = response.ChallengeParameters?.MFAS_CAN_SETUP
      ? JSON.parse(response.ChallengeParameters.MFAS_CAN_SETUP)
      : [];
    throw new AuthError(
      canSetup.length
        ? `This account has to set up MFA first (available: ${canSetup.join(', ')}). `
          + 'This console cannot run MFA enrollment yet — set it up for the user in the AWS '
          + 'console, or make MFA optional on the user pool.'
        : 'The user pool requires MFA but has no MFA method enabled, so nobody can sign in. '
          + 'Enable email codes (EMAIL_OTP) or authenticator codes (TOTP) in Cognito, '
          + 'or make MFA optional.',
    );
  }

  return {
    kind: 'challenge',
    challenge: {
      name,
      session: response.Session,
      username,
      prompt: spec?.prompt
        ?? `Cognito is asking for challenge ${name}, which this console has no screen for`,
      inputKind: spec?.inputKind ?? 'code',
      options,
    },
  };
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const response = await cognito('InitiateAuth', {
    ClientId: config.userPoolClientId,
    // 用户池是 ESSENTIALS 层级、AllowedFirstAuthFactors 只有 PASSWORD，
    // 所以走新版选择式流程并直接指定用密码做第一因子
    AuthFlow: 'USER_AUTH',
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
      PREFERRED_CHALLENGE: 'PASSWORD',
    },
  });
  return interpret(response, email);
}

export async function respondToChallenge(
  challenge: Challenge,
  answer: string,
): Promise<SignInResult> {
  const spec = CHALLENGE_SPEC[challenge.name];
  const responseKey = spec?.responseKey ?? 'ANSWER';

  const response = await cognito('RespondToAuthChallenge', {
    ClientId: config.userPoolClientId,
    ChallengeName: challenge.name,
    Session: challenge.session,
    ChallengeResponses: {
      USERNAME: challenge.username,
      [responseKey]: answer,
    },
  });
  return interpret(response, challenge.username);
}

export function hasAccess(session: Session) {
  return session.groups.some((g) => ALLOWED_GROUPS.includes(g));
}

/**
 * 只有这些异常代表 refresh token 本身废了（被吊销、过期、改过密码、用户被禁），
 * 清掉本地令牌是对的。除此之外的失败——断网、限流、Cognito 抽风——都是暂时的，
 * 清了的代价是用户得重走一遍邮箱验证码，所以宁可留着等下次。
 */
const DEAD_REFRESH_TOKEN = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'InvalidParameterException',
]);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function safeSession(tokens: Tokens): Session | null {
  try {
    return toSession(tokens);
  } catch {
    // 存的令牌解不开（写入被打断、被别的东西改过）。留着只会每次启动都抛同一个错，
    // 用户除了自己开控制台清 localStorage 没有别的出路
    clear();
    return null;
  }
}

async function refresh(refreshToken: string): Promise<Session | null> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await cognito('InitiateAuth', {
        ClientId: config.userPoolClientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      });
      // 续期不会下发新的 refresh token，fromAuthResult 会沿用本地那个
      if (!response.AuthenticationResult) return null;
      return fromAuthResult(response.AuthenticationResult);
    } catch (err) {
      if (err instanceof AuthError && DEAD_REFRESH_TOKEN.has(err.type)) {
        clear();
        return null;
      }
      if (attempt >= 2) return null;
      await sleep(400 * 2 ** attempt);
    }
  }
}

async function restore(): Promise<Session | null> {
  const tokens = load();
  if (!tokens?.accessToken) return null;

  // 留 5 分钟余量。贴着到期线才换令牌的话，上传大文件这种长请求会飞到一半失效
  if (tokens.expiresAt - Date.now() > 5 * 60_000) return safeSession(tokens);

  if (!tokens.refreshToken) {
    clear();
    return null;
  }
  return refresh(tokens.refreshToken);
}

let pending: Promise<Session | null> | null = null;

/**
 * 恢复会话；access token 快过期时用 refresh token 换新的。
 *
 * 并发去重是必须的：引导、每次 API 请求、转码轮询和签名续签定时器都会调它，
 * 令牌到期那一刻会同时打出好几个续期请求，很容易撞上 Cognito 限流，
 * 而限流以前会被当成"登录失效"把令牌清掉——这就是登录状态记不住的根源。
 */
export function restoreSession(): Promise<Session | null> {
  if (!pending) {
    // restore 内部已兜住所有异常，不会 reject，所以这里不需要再挂 catch
    pending = restore();
    pending.finally(() => { pending = null; });
  }
  return pending;
}

export function signOut() {
  const tokens = load();
  clear();
  // 用户池开了令牌吊销。不撤的话这个 refresh token 还能再用 30 天，
  // 共用电脑上点了退出却没真的退出。失败也无所谓，本地已经清了
  if (tokens?.refreshToken) {
    cognito('RevokeToken', {
      ClientId: config.userPoolClientId,
      Token: tokens.refreshToken,
    }).catch(() => {});
  }
}
